/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import axios from 'axios';
import { SecurityHelper } from 'sarsaparilla';
import * as globals from '../constants/globals';
import { CELL_CARRIER_OPTIONS } from '../constants/constants';
import type { aggregateResponse, cellAggregateRating } from '../model/aggregate';

type AiReviewSummary = {
    location_id: string;
    location_type: string;
    summary: string;
};

export function getAggregateReviews({
    locationId,
    locationType,
}: {
    locationId: string;
    locationType: string;
}) {
    return axios
        .get<aggregateResponse>(`${globals.API_URL}/aggregate`, {
            params: { location_id: locationId, location_type: locationType },
        })
        .then((response) => {
            // The backend may omit carriers with no ratings — backfill them
            // with zero-count entries so the UI can render a stable carrier
            // list. Mutates in place to match the legacy Redux behavior.
            const ratings: cellAggregateRating[] =
                response.data.aggregate_cell_coverage_ratings ?? [];
            CELL_CARRIER_OPTIONS.forEach((carrier) => {
                if (!ratings.some((r) => r.carrier === carrier)) {
                    ratings.push({
                        carrier,
                        average_rating: 0,
                        number_of_ratings: 0,
                    });
                }
            });
            return { ...response.data, aggregate_cell_coverage_ratings: ratings };
        });
}

export function getAIReviewSummary({
    locationId,
    locationType,
}: {
    locationId: string;
    locationType: string;
}) {
    return axios
        .get<AiReviewSummary>(
            `${globals.API_URL}/location/${encodeURIComponent(
                locationType
            )}/${encodeURIComponent(locationId)}/summary`,
            { timeout: 15_000 }
        )
        .then((response) => response.data);
}

export function postHelpfulAISummary({
    locationId,
    locationType,
}: {
    locationId: string;
    locationType: string;
}) {
    return axios.post(
        `/api/ratingreview/summary/helpful/vote`,
        {
            location_id: locationId,
            location_type: locationType,
            is_helpful: true,
            vote_changed: false,
        },
        { headers: { Authorization: SecurityHelper.getAuthHeader() } }
    );
}

export function postReportAISummary({
    locationId,
    locationType,
}: {
    locationId: string;
    locationType: string;
}) {
    return axios.post(
        `/api/ratingreview/summary/report`,
        { location_id: locationId, location_type: locationType },
        { headers: { Authorization: SecurityHelper.getAuthHeader() } }
    );
}

// cSpell:ignore ratingreview
